import { useState } from "react";
import { Info, ChevronLeft, ChevronRight } from "lucide-react";
import { INTERIOR_GALLERY } from "@/data/apartments";
import { useT } from "@/i18n/LanguageProvider";

export const InteriorGallery = () => {
    const t = useT();
    const [active, setActive] = useState(0);
    const total = INTERIOR_GALLERY.length;

    const prev = () => setActive((i) => (i - 1 + total) % total);
    const next = () => setActive((i) => (i + 1) % total);

    return (
        <section
            id="ausstattung"
            className="bg-stone-900 text-white py-24 sm:py-32"
            data-testid="interior-gallery"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-7">
                        <span className="overline text-clay-300">
                            {t({ de: "05 / 12 — Beispiel-Visualisierung · Loft", en: "05 / 12 — Sample visualisation · Loft" })}
                        </span>
                        <h2 className="h-section mt-4 text-white">
                            {t({ de: "So lässt sich der", en: "How the" })}
                            <span className="italic text-clay-300"> {t({ de: "Loft denken.", en: "loft can be thought." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="font-body text-base text-white/75 leading-relaxed">
                            {t({
                                de: "Die Innenfotos sind Beispiel-Visualisierungen für die Ausstattung des Loft-Hauses und sollen Inspiration und Größenverhältnisse vermitteln.",
                                en: "Interior photos are sample visualisations for the loft house, intended to convey inspiration and proportions.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="bg-clay-100/10 border border-white/15 px-5 py-4 mb-10 flex items-start gap-3" data-testid="interior-disclaimer">
                    <Info size={18} className="shrink-0 mt-0.5 text-clay-300" />
                    <p className="font-body text-sm text-white/85 leading-relaxed">
                        <span className="font-semibold text-white">{t({ de: "Hinweis:", en: "Note:" })}</span>{" "}
                        {t({
                            de: "Inventar nicht inbegriffen. Die abgebildete Einrichtung dient ausschließlich der Visualisierung und kann Sonder­ausstattung enthalten. Bodenbeläge und Malerarbeiten sind nicht Bestandteil der Leistung – die Wohnungen werden im erweiterten Ausbauzustand übergeben (Badezimmer gefliest).",
                            en: "Furniture not included. The shown interior is for visualisation only and may contain special equipment. Flooring and painting are not part of the deliverables – apartments are handed over in extended fit-out condition (bathrooms tiled).",
                        })}
                    </p>
                </div>

                <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
                    <div className="lg:col-span-3 relative aspect-[4/3] frame-img bg-stone-800">
                        <img
                            key={INTERIOR_GALLERY[active].url}
                            src={INTERIOR_GALLERY[active].url}
                            alt={INTERIOR_GALLERY[active].caption}
                            className="w-full h-full object-cover animate-fade-in"
                        />
                        <div className="absolute bottom-4 left-4 right-20 px-4 py-2 bg-stone-900/85 backdrop-blur-sm">
                            <span className="font-body text-sm">
                                {INTERIOR_GALLERY[active].caption}
                            </span>
                        </div>
                        <button
                            onClick={prev}
                            className="absolute top-1/2 left-4 -translate-y-1/2 p-3 bg-stone-900/70 backdrop-blur hover:bg-stone-900 transition-colors"
                            aria-label="Vorheriges Bild"
                            data-testid="interior-prev"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            onClick={next}
                            className="absolute top-1/2 right-4 -translate-y-1/2 p-3 bg-stone-900/70 backdrop-blur hover:bg-stone-900 transition-colors"
                            aria-label="Nächstes Bild"
                            data-testid="interior-next"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>

                    <div className="lg:col-span-2 grid grid-cols-2 gap-4">
                        {INTERIOR_GALLERY.map((item, i) => (
                            <button
                                key={item.url}
                                onClick={() => setActive(i)}
                                className={`frame-img aspect-[4/3] bg-stone-800 transition-all ${
                                    i === active
                                        ? "ring-2 ring-clay-300 ring-offset-2 ring-offset-stone-900"
                                        : "opacity-70 hover:opacity-100"
                                }`}
                                data-testid={`interior-thumb-${i}`}
                            >
                                <img src={item.url} alt={item.caption} className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
